import {
  TicketingOnboardingStateDocument,
  type TicketingOnboardingStateQuery,
} from 'generated/resources/graphql'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useTicketingPolicies } from 'modules/ticketing/hooks/useTicketingPolicies'

type OnboardingState =
  | { status: 'loading' }
  | { status: 'onboarding' }
  | { status: 'not_configured' }
  | { status: 'ready' }

interface UseTicketingOnboardingStateOptions {
  skip?: boolean
}

export const useTicketingOnboardingState = (
  options?: UseTicketingOnboardingStateOptions
): OnboardingState => {
  const skip = options?.skip ?? false

  const { loading: policiesLoading, canViewSettings, error: policiesError } = useTicketingPolicies()

  const {
    data,
    loading: categoriesLoading,
    error,
  } = useQuery<TicketingOnboardingStateQuery, Record<string, never>>(
    TicketingOnboardingStateDocument,
    {
      fetchPolicy: 'cache-and-network',
      skip,
    }
  )

  if (skip) {
    return { status: 'ready' }
  }

  if (policiesLoading || categoriesLoading) {
    return { status: 'loading' }
  }

  // If either query errored, fall through to ready state to show the normal ticket list
  if (error || policiesError) {
    return { status: 'ready' }
  }

  const categoryCount = data?.ticketing?.ticketCategoriesConnection?.totalCount ?? 0

  if (categoryCount > 0) {
    return { status: 'ready' }
  }

  if (canViewSettings) {
    return { status: 'onboarding' }
  }

  return { status: 'not_configured' }
}
